import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AxisSpinner from "../components/AxisSpinner";
import ShiftInvoiceElement from "../components/ShiftInvoiceElement";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { AxisLineSeparator } from "../components/AxisLineSeparator";
import { AxisDate } from "../util/AxisDateTime";
import { BsArrowRight, BsFillCalculatorFill, BsLayoutSidebar, BsLayoutSidebarReverse } from "react-icons/bs";
import { useLayout } from "../context/LayoutContext";

const TimesheetPdfPanel = ({ invoice }) => {
  const { authAxios } = useAuth();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!invoice?.timesheet?.id) return;
    setPdfLoading(true);
    setPdfError(false);

    authAxios()
      .get(`/api/backoffice/timesheet/timesheet_file`, {
        params: { timesheet_id: invoice.timesheet.id },
        responseType: "blob",
      })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        blobUrlRef.current = url;
        setPdfUrl(url);
      })
      .catch(() => setPdfError(true))
      .finally(() => setPdfLoading(false));

    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [invoice?.timesheet?.id]);

  return (
    <Fragment>
      {pdfLoading && <AxisSpinner loading={true} />}
      {pdfError && (
        <div className="alert alert-warning">Timesheet PDF not available.</div>
      )}
      {pdfUrl && !pdfLoading && (
        <iframe
          src={pdfUrl}
          title="Timesheet PDF"
          style={{
            width: "100%",
            height: "calc(100vh - 260px)",
            minHeight: "600px",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
          }}
        />
      )}
    </Fragment>
  );
};

const InvoiceShift = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { sidebarHidden, setSidebarHidden } = useLayout();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [nextInvoiceId, setNextInvoiceId] = useState(null);
  const [totalRemaining, setTotalRemaining] = useState(null);
  const [trustShortName, setTrustShortName] = useState(null);
  const [weekEnding, setWeekEnding] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    document.title = "Invoice";
    fetchInvoice();
  }, [invoiceId]);

  useEffect(() => {
    return () => setSidebarHidden(false);
  }, [setSidebarHidden]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setNextInvoiceId(null);
      setTotalRemaining(null);
      setTrustShortName(null);
      setWeekEnding(null);
      setRole(null);
      const response = await authAxios().get(
        `/api/backoffice/invoice/${invoiceId}`,
      );
      const data = response.data.invoice;
      const timesheetWeekId = response.data.timesheetWeekId;
      const gradeType = response.data.gradeType || null;
      setInvoice(data);
      setWeekEnding(data?.timesheet?.weekEnding || null);
      setRole(gradeType);
      if (data.archiveShiftId) {
        const shiftResp = await authAxios().get(
          "/api/backoffice/shifts/archive",
          {
            params: { shift_id: data.archiveShiftId },
          },
        );
        setTrustShortName(shiftResp.data?.trust?.shortName || null);
        fetchNextInvoice(data.id, shiftResp.data?.trust?.id, timesheetWeekId, gradeType);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNextInvoice = async (currentId, trustId, timesheetWeekId, gradeType) => {
    if (!trustId) return;
    try {
      const params = { status: "CREATED", trust_id: trustId };
      if (timesheetWeekId) params.timesheetWeek = timesheetWeekId;
      if (gradeType) params.role = gradeType;
      const resp = await authAxios().post(
        "/api/backoffice/invoice/paging",
        { direction: "asc", key: "created", page: 0, elementPerPage: 10 },
        { params },
      );
      const next = resp.data?.content?.find(
        (row) => row?.invoice?.id !== currentId,
      );
      setTotalRemaining(resp.data?.totalElements ?? null);
      setNextInvoiceId(next?.invoice?.id || null);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Fragment>
      <Row className={"icon-text-wrapper"}>
        <Col>
          <h1 className={"text-axis-offblack icon-text-wrapper"}>
            <BsFillCalculatorFill className="me-2" />
            Invoicing for{" "}
            {invoice?.accountRef ||
              invoice?.bookingRef ||
              `#${invoiceId}`} — {invoice?.bookingRef || ""}
          </h1>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Button
            variant="outline-secondary"
            className={"icon-text-wrapper me-2 d-none d-md-inline-flex"}
            onClick={() => setSidebarHidden(!sidebarHidden)}
            title={sidebarHidden ? "Show sidebar" : "Hide sidebar for more room"}
          >
            {sidebarHidden ? <BsLayoutSidebar className="me-1" /> : <BsLayoutSidebarReverse className="me-1" />}
            {sidebarHidden ? "Show sidebar" : "Focus mode"}
          </Button>
        </Col>
        {nextInvoiceId && totalRemaining != null && (
          <Col xs="auto" className="d-flex align-items-center text-muted me-2">
            <span className="font-weight-bold text-axis-offblack">{totalRemaining - 1}</span>
            {role && (
              <span className="ms-1 font-weight-bold text-axis-offblack">{role}</span>
            )}
            <span className="ms-1">remaining</span>
            {trustShortName && (
              <Fragment>
                <span className="ms-1">for</span>
                <span className="ms-1 font-weight-bold text-axis-offblack">{trustShortName}</span>
              </Fragment>
            )}
            {weekEnding && (
              <Fragment>
                <span className="ms-1">week ending</span>
                <span className="ms-1 font-weight-bold text-axis-offblack">{AxisDate(weekEnding)}</span>
              </Fragment>
            )}
          </Col>
        )}
        {nextInvoiceId && (
          <Col xs="auto" className="d-flex align-items-center">
            <Button
              className={"shift-button-new-shift icon-text-wrapper"}
              onClick={() => navigate(`/invoice/${nextInvoiceId}`)}
            >
              Next <BsArrowRight className="ms-1" />
            </Button>
          </Col>
        )}
      </Row>
      <AxisLineSeparator />
      {loading ? (
        <AxisSpinner loading={true} />
      ) : (
        <Container fluid>
          <Row>
            <Col md={6} lg={5} xl={5}>
              <ShiftInvoiceElement invoiceId={parseInt(invoiceId)} />
            </Col>
            <Col md={6} lg={7} xl={7}>
              <TimesheetPdfPanel invoice={invoice} />
              {invoice?.archiveShiftId && (
                <div className="mt-3">
                  <Button
                    as="a"
                    href={`/archiveShifts/view/${invoice.archiveShiftId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="shift-button-approve icon-text-wrapper justify-content-center w-100"
                  >
                    View Timesheet Page
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      )}
    </Fragment>
  );
};

export default InvoiceShift;
